import { lazy, useEffect, useState } from 'react'
import { BackBtn, NVButton } from '@nv/basic-components'
import { EventCountdownTimeDateContainer, EventStateStatus, Skeleton, useNVModal } from '@nv/components'
import { useVoterUserContext } from '@nv/contexts'
import { useCheckVotingOpenStateChange } from '@nv/hooks'
import BalletBoxIcon from '@nv/images/design/ballet-box-icon'
import ClockIcon from '@nv/images/design/clock-icon'
import DividerIcon from '@nv/images/design/divider-icon'
import ReceiptIcon from '@nv/images/design/reciept-icon'
import { VoterAuthService } from '@nv/services'
import { STATE_STATUSES, beforeUnload, formatDateLocale, getEventStateStatus, nxConfig } from '@nv/utils'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { VoterEventDetailsAttachments } from './voter_event_details_attachments/VoterEventDetailsAttachments'
import styles from './VoterEventDetails.module.scss'
const VerifyVoterReceipt = lazy(() => import('@nv/lazy-loadable/lib/modals/VerifyVoterReceipt'))

export const VoterEventDetails = () => {
  const { eventId } = useParams()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const [event, setEvent]: any = useState(location.state.event)
  const [listOfDocs, setListOfDocs] = useState([] as any)
  const { voterUser } = useVoterUserContext()
  const hasVoted = voterUser?.votedInEvents?.includes(eventId)
  const formattedCloseDateTime = formatDateLocale(event?.closeDate, currentLanguage)
  const { NVModal: VerifyVoterReceiptModal, openModal: openVerifyVoterReceiptModal } = useNVModal()

  const getEvents = async () => {
    const resp = await VoterAuthService.getEventById(eventId)
    if (resp) setEvent(resp)
  }
  const { votingOpen, loadingVotingOpen } = useCheckVotingOpenStateChange({
    event: event,
    closeDate: event?.closeDate,
    fetchEvent: getEvents,
  })
  const eventStateStatus = getEventStateStatus(event?.state, votingOpen, event?.nominationState)

  // TO debug
  const getHelpDocs = async () => {
    try {
      const resp = await VoterAuthService.getHelpDocsEvent(event.id)
      if (resp) {
        setListOfDocs(resp)
      }
    } catch (error) {
      //console.log(error)
    }
  }

  useEffect(() => {
    getEvents()
  }, [])

  useEffect(() => {
    getHelpDocs()
  }, [event])

  if (!event) return null

  return (
    <>
      {nxConfig.voteReceiptEnabled && (
        <VerifyVoterReceiptModal>
          <VerifyVoterReceipt event={event} />
        </VerifyVoterReceiptModal>
      )}

      <div className={styles['container']}>
        <div className={styles['back-btn']}>
          <BackBtn navigateTo={'/voting/voter/dashboard'} />
          <span className={styles['back-btn__heading']}>{t('pages.eventDetails')}</span>
        </div>

        <div className={styles['content']}>
          <div className={styles['content__section']}>
            <BalletBoxIcon />
            <div className={styles['content__description']}>
              <div className={styles['content__header']}>
                <div className={styles['content__title']}>{event.name[currentLanguage]}</div>
                <DividerIcon style={{ marginRight: '1.25rem', marginLeft: '1.25rem' }} />
                {!loadingVotingOpen ? <EventStateStatus eventStatus={eventStateStatus} /> : <Skeleton width={150} />}
              </div>

              {!loadingVotingOpen ? event.description ? event.description[currentLanguage] : '' : <Skeleton width={500} />}
            </div>
          </div>

          <div className={styles['content__section']}>
            <ClockIcon />
            <div className={styles['content__card__container']}>
              <div className={styles['content__card']}>
                <div className={styles['content__label']}>
                  {eventStateStatus !== STATE_STATUSES.closed ? t('countdown.label.willClose') : t('countdown.label.closed')}
                </div>
                <div className={styles['content__boldText']}>{!loadingVotingOpen ? formattedCloseDateTime : <Skeleton width={150} />}</div>
              </div>

              <div className={styles['content__card']}>
                <div className={styles['content__label']}>{t('events.voters.hasVotedLabel')}</div>
                <div
                  className={clsx(styles['content__status'], {
                    [styles['content__status__active']]: hasVoted,
                  })}
                >
                  {!loadingVotingOpen ? !hasVoted ? t('events.voters.hasNotVoted') : t('events.voters.voted') : <Skeleton width={150} />}
                </div>
              </div>
            </div>
          </div>

          {nxConfig.voteReceiptEnabled && (
            <div className={styles['content__verify']}>
              {!loadingVotingOpen ? (
                <NVButton
                  variant='text'
                  onClick={() => openVerifyVoterReceiptModal()}
                  icon={<ReceiptIcon />}
                  text={t('events.voteReceipt.verifyVoteReceipt')}
                  classes={{ container: styles['button'] }}
                />
              ) : (
                <Skeleton width={150} />
              )}
            </div>
          )}

          <div
            className={clsx(styles['content__date'], {
              [styles['content__date__closed']]: eventStateStatus === STATE_STATUSES.closed,
            })}
          >
            {!loadingVotingOpen ? (
              <EventCountdownTimeDateContainer event={event} votingOpen={votingOpen} size='large' layout='row' hideFormattedDate />
            ) : (
              <Skeleton width={350} />
            )}
            {loadingVotingOpen ? (
              <Skeleton width={125} />
            ) : votingOpen && (!hasVoted || event.multipleVotesEnabled) ? (
              <NVButton
                text={t('events.voters.voteNow')}
                onClick={() => {
                  VoterAuthService.updateUserTiviAuthenticated({
                    tiviId: event.tiviId,
                  })
                  window.removeEventListener('beforeunload', beforeUnload)
                  window.location.href = `${event.eventLink}?locale=${currentLanguage || 'en'}`
                }}
                height={50}
              />
            ) : null}
          </div>
        </div>
        <VoterEventDetailsAttachments listOfDocs={listOfDocs} getHelpDocs={getHelpDocs} />
      </div>
    </>
  )
}
